import { z } from 'zod';
import { personAddressSchema } from './details.js';
import { canonicalize } from '../hashing/canonical.js';
import { findStepType } from '../steps/registry.js';
import { personSchema, updatePersonSchema } from './person.js';
import { cleanProfileFieldValues, isBlankProfileValue, isDeliveryProfileField, isPersonProfileField, profileFieldSchema, profileFieldValueSchemas, } from './profile-fields.js';
/**
 * Candidate answers "on the profile" — a projection, never a copy (ADR-013 §1).
 *
 * Nothing is written to Person when a candidate types. The profile's candidate-supplied facts
 * are computed here from the Person record and the person's StepInstances, and whether a value
 * is reviewed is derived from the instance's state. No `reviewed` flag exists anywhere, which is
 * what keeps CLAUDE.md's derived-state rule literally true.
 *
 * Nothing downstream may decide on a `pending` value. Requirement evaluation reads approved
 * submissions and HR-recorded evidence only (ADR-006, ADR-008); this projection is for people to
 * read.
 */
const factTimeSchema = z.string().datetime();
/** What the app acts on: HR's value or an approved answer, whichever was reviewed last. */
export const effectiveProfileValueSchema = z.object({
    value: z.unknown(),
    source: z.enum(['hr', 'candidate']),
    at: factTimeSchema,
    instanceId: z.string().min(1).optional(),
}).strict();
/**
 * How far an answer that is not effective has got. The first three are ADR-013 §1's table.
 * `approved` is the delivery case (Q1): the form was approved, but a delivery email or phone is
 * never adopted by approval, so it waits for HR's "Use the candidate's answer".
 */
export const pendingReviewSchema = z.enum(['in-progress', 'sent', 'returned', 'approved']);
export const pendingProfileValueSchema = z.object({
    value: z.unknown(),
    review: pendingReviewSchema,
    savedAt: factTimeSchema,
    instanceId: z.string().min(1),
}).strict();
export const profileFactSchema = z.object({
    field: profileFieldSchema,
    effective: effectiveProfileValueSchema.optional(),
    pending: pendingProfileValueSchema.optional(),
}).strict();
/**
 * One instance's answers, and how reviewed they are, per ADR-013 §1's table. Instances with
 * nothing typed, not yet sent, or withdrawn contribute nothing.
 */
function answersOf(instance) {
    switch (instance.state) {
        case 'sent':
        case 'in_progress':
            return instance.draft ? { value: instance.draft.value, at: instance.draft.savedAt, review: 'in-progress' } : null;
        case 'submitted':
            return instance.submittedAt ? { value: instance.submission, at: instance.submittedAt, review: 'sent' } : null;
        case 'rejected':
            // Returned to the candidate: what they are fixing, else what HR sent back.
            if (instance.draft)
                return { value: instance.draft.value, at: instance.draft.savedAt, review: 'returned' };
            return instance.submittedAt ? { value: instance.submission, at: instance.submittedAt, review: 'returned' } : null;
        case 'approved':
            // An approval with no time cannot be ordered against an HR edit, so it cannot win one.
            return instance.reviewedAt ? { value: instance.submission, at: instance.reviewedAt, review: 'reviewed' } : null;
        case 'not_started':
        case 'cancelled':
            return null;
    }
}
function candidateValues(instances) {
    const byField = new Map();
    for (const instance of instances) {
        const module = findStepType(instance.type);
        const answers = module ? answersOf(instance) : null;
        if (!module || !answers)
            continue;
        for (const { field, value } of module.runtime.profileFields(instance.config, answers.value)) {
            const list = byField.get(field) ?? [];
            list.push({ value, instanceId: instance.id, at: answers.at, review: answers.review });
            byField.set(field, list);
        }
    }
    return byField;
}
function readPath(source, path) {
    return path.split('.').reduce((node, key) => (typeof node === 'object' && node !== null ? node[key] : undefined), source);
}
/** A changed path speaks to a field when either contains the other (`contact` covers `contact.address`). */
function touches(changed, field) {
    return changed === field || changed.startsWith(`${field}.`) || field.startsWith(`${changed}.`);
}
function hrEditTime(person, field, edits) {
    if (edits === undefined)
        return person.updatedAt;
    let at = person.createdAt;
    for (const edit of edits) {
        const sets = edit.action === 'person.created' || (edit.action === 'person.updated' && edit.changedFields.some((changed) => touches(changed, field)));
        if (sets && edit.occurredAt > at)
            at = edit.occurredAt;
    }
    return at;
}
/** HR's current value, when the field lives on Person and holds something. */
function hrValue(person, field) {
    if (!isPersonProfileField(field))
        return undefined;
    const [clean] = cleanProfileFieldValues([{ field, value: readPath(person, field) }]);
    return clean?.value;
}
// Values here came out of Zod schemas over JSON, so they are JSON by construction; the
// round trip drops `undefined` keys, which canonicalize would otherwise reject.
const sameValue = (a, b) => canonicalize(JSON.parse(JSON.stringify(a ?? null))) === canonicalize(JSON.parse(JSON.stringify(b ?? null)));
const latest = (values) => values.reduce((best, next) => (best === undefined || next.at > best.at ? next : best), undefined);
/**
 * Every profile field a form maps to, with its effective and pending value (ADR-013 §1).
 *
 * - **Effective** is the latest REVIEWED fact: HR's own value, dated by its `person.updated`
 *   row, against an approved answer, dated by the approval's `reviewedAt`. A tie goes to HR.
 * - An unreviewed answer is **never** effective. A delivery email or phone is never effective
 *   from a candidate even when approved (Q1).
 * - **Pending** is the newest answer that is not the effective one, shown only when it differs
 *   from the effective value, so HR sees "Candidate sent a change, not reviewed".
 *
 * Pure, and it iterates the registry: it never names a step type (CLAUDE.md rule 6).
 */
export function projectProfileAnswers(person, instances, options = {}) {
    const fromCandidates = candidateValues(instances.filter((instance) => instance.personId === person.id));
    return profileFieldSchema.options.flatMap((field) => {
        const answers = fromCandidates.get(field) ?? [];
        const hr = hrValue(person, field);
        const hrFact = isBlankProfileValue(hr) ? undefined : { value: hr, at: hrEditTime(person, field, options.hrEdits) };
        const approved = isDeliveryProfileField(field) ? undefined : latest(answers.filter((answer) => answer.review === 'reviewed'));
        const effective = approved !== undefined && (hrFact === undefined || approved.at > hrFact.at)
            ? { value: approved.value, source: 'candidate', at: approved.at, instanceId: approved.instanceId }
            : hrFact && { value: hrFact.value, source: 'hr', at: hrFact.at };
        // Unreviewed answers, plus — for a delivery field only — approved ones, which approval
        // cannot adopt. An approved answer that lost to a newer HR edit is history, not pending.
        const newest = latest(answers.filter((answer) => answer.review !== 'reviewed' || isDeliveryProfileField(field)));
        const pending = newest !== undefined && !(effective && sameValue(newest.value, effective.value))
            ? {
                value: newest.value,
                review: newest.review === 'reviewed' ? 'approved' : newest.review,
                savedAt: newest.at,
                instanceId: newest.instanceId,
            }
            : undefined;
        if (!effective && !pending)
            return [];
        return [{ field, ...(effective ? { effective } : {}), ...(pending ? { pending } : {}) }];
    });
}
/**
 * A person as `GET /api/people/{id}` serves one (#212): the record, and beside it the candidate
 * answers projected onto its profile. `profileAnswers` is DERIVED on every read by
 * `projectProfileAnswers` and never stored on the person; it is empty where no form has been
 * answered, and where the server has no portal. Optional in the type so a server that does not send it
 * yet still parses and a record without it is still a person.
 */
export const personWithProfileAnswersSchema = personSchema.extend({
    profileAnswers: z.array(profileFactSchema).optional(),
});
/**
 * The patch that makes the candidate's answer HR's value (ADR-013 §2): an ordinary `people.update`,
 * which the server audits as `person.updated` with the changed field names and the reason, never
 * the value. Null for a field that lives only on the application (history, references, education,
 * credentials), a value that is not the shape the field takes, or one the person record would refuse
 * (the profile vocabulary allows a middle name of 500 characters, the record 200). Never throws: it is
 * asked while a page draws, so a refused value is no button, not a broken page.
 *
 * An address is sent whole: the update merges nested objects key by key, so a key the candidate
 * left out would otherwise keep HR's old text under the candidate's new street.
 */
export function adoptionPatch(field, value) {
    if (!isPersonProfileField(field))
        return null;
    const parsed = profileFieldValueSchemas[field].safeParse(value);
    if (!parsed.success)
        return null;
    const adopted = parsed.data;
    const raw = (() => {
        switch (field) {
            case 'name.givenName': return { name: { givenName: adopted } };
            case 'name.middleName': return { name: { middleName: adopted } };
            case 'name.familyName': return { name: { familyName: adopted } };
            case 'identity.previousName': return { identity: { previousName: adopted } };
            case 'contact.email': return { contact: { email: adopted } };
            case 'contact.phone': return { contact: { phone: adopted } };
            case 'contact.alternatePhone': return { contact: { alternatePhone: adopted } };
            case 'contact.preferredCallTime': return { contact: { preferredCallTime: adopted } };
            case 'contact.address': {
                const answer = adopted;
                return { contact: { address: Object.fromEntries(Object.keys(personAddressSchema.shape).map((key) => [key, answer[key] ?? null])) } };
            }
            case 'contact.emergencyContacts': return { contact: { emergencyContacts: adopted } };
            default: return undefined;
        }
    })();
    const checked = updatePersonSchema.safeParse(raw);
    return checked.success ? checked.data : null;
}
/**
 * What the person record holds for a profile field, cleaned the way the projection reads it; undefined for a field the
 * record has no place for, or one that is blank. For a page to ask whether an answer is already on the record.
 */
export const profileRecordValue = (person, field) => hrValue(person, field);
/** Whether two profile values are the same value (key order irrelevant, list order significant). */
export const sameProfileValue = (a, b) => sameValue(a, b);
//# sourceMappingURL=profile.js.map